import { useRef, useState } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Box,
    Table, TableBody, TableCell, TableHead, TableRow, Chip, Typography, CircularProgress,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";
import { axiosInstance } from "../utility/apiInstance";


const TEMPLATE_HEADERS = [
    "Salutation", "Name", "Organization", "Country Code",
    "WhatsApp No.", "Email ID", "Flags", "Allowed Channels",
];

// map display header -> field key returned by the backend
const FIELD_BY_HEADER: Record<string, string> = {
    "Salutation": "salutation",
    "Name": "name",
    "Organization": "organization",
    "Country Code": "countryCode",
    "WhatsApp No.": "whatsappNumber",
    "Email ID": "email",
    "Flags": "flags",
    "Allowed Channels": "allowedChannels",
};
// verify the uploaded sheet has exactly the template columns
const validateHeaders = (rows: any[]): boolean => {
    const fileHeaders = Object.keys(rows[0] ?? {}).map((h) => h.trim());
    const missing = TEMPLATE_HEADERS.filter((h) => !fileHeaders.includes(h));
    return missing.length === 0;
};

type RowData = {
    rowNumber: number;
    salutation?: string; name?: string; organization?: string;
    countryCode?: string; whatsappNumber?: string; email?: string;
    flags?: string; allowedChannels?: string;
};
type ValidationRow = RowData & { valid: boolean; reasons: string[] };
type CreatedRow = RowData & { id: string };
type FailedRow = RowData & { reasons: string[] };
type ResultFilter = "all" | "created" | "failed";

export default function BulkUploadContactsDialog({
    open, onClose, onCompleted,
}: { open: boolean; onClose: () => void; onCompleted: () => void }) {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [step, setStep] = useState<"select" | "review" | "result">("select");
    const [loading, setLoading] = useState(false);
    const [rawRows, setRawRows] = useState<any[]>([]);
    const [validation, setValidation] = useState<{ total: number; validCount: number; invalidCount: number; rows: ValidationRow[] }>();
    const [result, setResult] = useState<{ createdCount: number; failedCount: number; created: CreatedRow[]; failed: FailedRow[] }>();
    const [resultFilter, setResultFilter] = useState<ResultFilter>("all");
    const [reviewFilter, setReviewFilter] = useState<"all" | "valid" | "invalid">("all");

    const reset = () => {
        setStep("select"); setRawRows([]); setValidation(undefined); setResult(undefined);
        setResultFilter("all"); setReviewFilter("all"); setLoading(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleClose = () => { reset(); onClose(); };

    const downloadTemplate = () => {
        const ws = XLSX.utils.aoa_to_sheet([
            TEMPLATE_HEADERS,
            ["Mr", "John Doe", "Acme Logistics", "91", "9876543210", "john@acme.com", "VIP, Priority", "email, whatsapp"],
        ]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Contacts");
        XLSX.writeFile(wb, "contact_bulk_upload_template.xlsx");
    };

    const parseFile = (file: File): Promise<any[]> =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const wb = XLSX.read(e.target?.result, { type: "array" });
                    const ws = wb.Sheets[wb.SheetNames[0]];
                    resolve(XLSX.utils.sheet_to_json(ws, { defval: "" }));
                } catch (err) { reject(err); }
            };
            reader.onerror = reject;
            reader.readAsArrayBuffer(file);
        });

    const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            setLoading(true);
            const json = await parseFile(file);
            if (!json.length) { toast.error("The file has no data rows"); setLoading(false); return; }
            if (!validateHeaders(json)) {
                toast.error("This file doesn't match the template. Please download the template, fill it, and upload again.");
                setLoading(false);
                if (fileInputRef.current) fileInputRef.current.value = "";
                return;
            }
            setRawRows(json);
            const { data } = await axiosInstance.post("/api/v1/contact/bulk-upload/validate", { rows: json });
            setValidation(data?.data);
            setReviewFilter("all");
            setStep("review");
        } catch (err: any) {
            toast.error(err?.response?.data?.message || "Could not read the file");
        } finally {
            setLoading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const handleConfirm = async () => {
        if (!validation) return;
        // send only the raw rows that passed validation
        const validRaw = rawRows.filter((_, i) => validation.rows[i]?.valid);
        if (!validRaw.length) return;
        try {
            setLoading(true);
            const { data } = await axiosInstance.post("/api/v1/contact/bulk-upload/create", { rows: validRaw });
            setResult(data?.data);
            setResultFilter("all");
            setStep("result");
            toast.success(`${data?.data?.createdCount} contact(s) created`);
        } catch (err: any) {
            toast.error(err?.response?.data?.message || "Upload failed");
        } finally {
            setLoading(false);
        }
    };

    const AMBER = "#F5A524";
    const cellSx = { fontSize: "0.8rem", whiteSpace: "nowrap", verticalAlign: "middle" } as const;

    // build a combined, filterable list for the result step
    const resultRows = result
        ? [
            ...result.created.map((r) => ({ ...r, _status: "created" as const })),
            ...result.failed.map((r) => ({ ...r, _status: "failed" as const })),
        ]
            .sort((a, b) => a.rowNumber - b.rowNumber)
            .filter((r) => resultFilter === "all" || r._status === resultFilter)
        : [];

    const EmptyState = ({ text }: { text: string }) => (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 200,
                width: "100%",
            }}
        >
            <Typography variant="body2" color="text.secondary">{text}</Typography>
        </Box>
    );



    const renderFieldTable = (
        rows: Array<RowData & { _status?: "created" | "failed"; valid?: boolean; reasons?: string[] }>,
        lastCol: "status" | "reason" | "both",
    ) => (
        <Box sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 900 }}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>#</TableCell>
                        {TEMPLATE_HEADERS.map((h) => (
                            <TableCell key={h} sx={{ whiteSpace: "nowrap" }}>{h}</TableCell>
                        ))}
                        {(lastCol === "reason" || lastCol === "both") && <TableCell sx={{ whiteSpace: "nowrap" }}>Reason</TableCell>}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((r) => {
                        const isError = r._status === "failed" || r.valid === false;
                        return (
                            <TableRow
                                key={`${r._status || ""}-${r.rowNumber}`}
                                sx={{
                                    height: 40,
                                    backgroundColor: isError ? "#fff5f5" : "transparent",
                                }}
                            >
                                <TableCell sx={cellSx}>{r.rowNumber}</TableCell>
                                {TEMPLATE_HEADERS.map((h) => (
                                    <TableCell key={h} sx={cellSx}>{(r as any)[FIELD_BY_HEADER[h]] ?? ""}</TableCell>
                                ))}

                                {(lastCol === "reason" || lastCol === "both") && (
                                    <TableCell sx={{ fontSize: "0.8rem", minWidth: 220 }}>{(r.reasons || []).join("; ")}</TableCell>
                                )}
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </Box>
    );

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ fontWeight: 700 }}>Bulk Upload Contacts</DialogTitle>
            <DialogContent dividers>
                {step === "select" && (
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 3 }}>
                        <Typography variant="body2" color="text.secondary" textAlign="center">
                            Download the template, fill one contact per row, then upload it back.
                            Flags and Allowed Channels are comma-separated.
                        </Typography>
                        <Button startIcon={<DownloadIcon />} variant="outlined" onClick={downloadTemplate}>
                            Download Template
                        </Button>
                        <Button
                            startIcon={loading ? <CircularProgress size={18} /> : <UploadFileIcon />}
                            variant="contained"
                            disabled={loading}
                            onClick={() => fileInputRef.current?.click()}
                            sx={{ backgroundColor: AMBER, "&:hover": { backgroundColor: "#DB9115" } }}
                        >
                            {loading ? "Reading..." : "Select File"}
                        </Button>
                        <input
                            ref={fileInputRef} type="file" hidden
                            accept=".xlsx,.xls,.csv"
                            onChange={handleFile}
                        />
                    </Box>
                )}

                {step === "review" && validation && (
                    <Box>
                        {/* clickable filter chips */}
                        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                            <Chip
                                label={`Total: ${validation.total}`}
                                onClick={() => setReviewFilter("all")}
                                variant={reviewFilter === "all" ? "filled" : "outlined"}
                                sx={reviewFilter === "all" ? { backgroundColor: "#eee", fontWeight: 600 } : {}}
                            />
                            <Chip
                                label={`Valid: ${validation.validCount}`}
                                onClick={() => setReviewFilter("valid")}
                                variant={reviewFilter === "valid" ? "filled" : "outlined"}
                                sx={{
                                    backgroundColor: reviewFilter === "valid" ? "#e8f5e9" : "transparent",
                                    color: "#2e7d32", fontWeight: reviewFilter === "valid" ? 600 : 400,
                                    borderColor: "#2e7d32",
                                }}
                            />
                            <Chip
                                label={`Invalid: ${validation.invalidCount}`}
                                onClick={() => setReviewFilter("invalid")}
                                variant={reviewFilter === "invalid" ? "filled" : "outlined"}
                                sx={{
                                    backgroundColor: reviewFilter === "invalid" ? "#fdecea" : "transparent",
                                    color: "#c62828", fontWeight: reviewFilter === "invalid" ? 600 : 400,
                                    borderColor: "#c62828",
                                }}
                            />
                        </Box>
                        {(() => {
                            const rows = validation.rows.filter((r) =>
                                reviewFilter === "all" ? true : reviewFilter === "valid" ? r.valid : !r.valid
                            );
                            return rows.length > 0
                                ? renderFieldTable(rows, "both")
                                : <EmptyState text="No rows to show." />;
                        })()}
                    </Box>
                )}

                {step === "result" && result && (
                    <Box>
                        {/* clickable filter chips */}
                        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                            <Chip
                                label={`All: ${result.createdCount + result.failedCount}`}
                                onClick={() => setResultFilter("all")}
                                variant={resultFilter === "all" ? "filled" : "outlined"}
                                sx={resultFilter === "all" ? { backgroundColor: "#eee", fontWeight: 600 } : {}}
                            />
                            <Chip
                                label={`Created: ${result.createdCount}`}
                                onClick={() => setResultFilter("created")}
                                variant={resultFilter === "created" ? "filled" : "outlined"}
                                sx={{
                                    backgroundColor: resultFilter === "created" ? "#e8f5e9" : "transparent",
                                    color: "#2e7d32", fontWeight: resultFilter === "created" ? 600 : 400,
                                    borderColor: "#2e7d32",
                                }}
                            />
                            <Chip
                                label={`Failed: ${result.failedCount}`}
                                onClick={() => setResultFilter("failed")}
                                variant={resultFilter === "failed" ? "filled" : "outlined"}
                                sx={{
                                    backgroundColor: resultFilter === "failed" ? "#fdecea" : "transparent",
                                    color: "#c62828", fontWeight: resultFilter === "failed" ? 600 : 400,
                                    borderColor: "#c62828",
                                }}
                            />
                        </Box>
                        {resultRows.length > 0
                            ? renderFieldTable(resultRows, "both")
                            : <EmptyState text="No rows to show." />}
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                {step === "review" && (
                    <>
                        <Button onClick={reset}>Back</Button>
                        <Button
                            variant="contained"
                            disabled={loading || !validation?.validCount}
                            onClick={handleConfirm}
                            sx={{ backgroundColor: AMBER, "&:hover": { backgroundColor: "#DB9115" } }}
                        >
                            {loading ? "Creating..." : `Create ${validation?.validCount} Contact(s)`}
                        </Button>
                    </>
                )}
                {step === "result" && (
                    <Button variant="contained" onClick={() => { onCompleted(); handleClose(); }}
                        sx={{ backgroundColor: AMBER, "&:hover": { backgroundColor: "#DB9115" } }}>
                        Done
                    </Button>
                )}
                {step === "select" && <Button onClick={handleClose}>Cancel</Button>}
            </DialogActions>
        </Dialog>
    );
}